import React, { useState, createContext, useContext } from 'react';
// Define the shape of our user data
export interface UserProgress {
  streak: number;
  completedToday: number;
  phase: string;
  weekNumber: number;
}
export interface MentalState {
  energy: number;
  positivity: number;
  lastCheckIn: string;
}
export interface UserActivity {
  id: string;
  title: string;
  timestamp: string;
  type: 'exercise' | 'diary' | 'check-in';
}
interface UserDataContextType {
  userProgress: UserProgress;
  mentalState: MentalState;
  recentActivities: UserActivity[];
  updateUserProgress: (data: Partial<UserProgress>) => void;
  updateMentalState: (data: Partial<MentalState>) => void;
  addActivity: (activity: UserActivity) => void;
}
// Create the context with default values
const UserDataContext = createContext<UserDataContextType>({
  userProgress: {
    streak: 3,
    completedToday: 2,
    phase: 'Energie-herstel',
    weekNumber: 3
  },
  mentalState: {
    energy: 70,
    positivity: 60,
    lastCheckIn: '2 dagen geleden'
  },
  recentActivities: [{
    id: 'act-1',
    title: 'Ademhalingsoefening voltooid',
    timestamp: 'Gisteren, 20:15',
    type: 'exercise'
  }, {
    id: 'act-2',
    title: 'Dagboeknotitie toegevoegd',
    timestamp: '3 dagen geleden',
    type: 'diary'
  }],
  updateUserProgress: () => {},
  updateMentalState: () => {},
  addActivity: () => {}
});
// Provider component that wraps the app
export const UserDataProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [userProgress, setUserProgress] = useState<UserProgress>({
    streak: 3,
    completedToday: 2,
    phase: 'Energie-herstel',
    weekNumber: 3
  });
  const [mentalState, setMentalState] = useState<MentalState>({
    energy: 70,
    positivity: 60,
    lastCheckIn: '2 dagen geleden'
  });
  const [recentActivities, setRecentActivities] = useState<UserActivity[]>([{
    id: 'act-1',
    title: 'Ademhalingsoefening voltooid',
    timestamp: 'Gisteren, 20:15',
    type: 'exercise'
  }, {
    id: 'act-2',
    title: 'Dagboeknotitie toegevoegd',
    timestamp: '3 dagen geleden',
    type: 'diary'
  }]);
  // Update functions
  const updateUserProgress = (data: Partial<UserProgress>) => {
    setUserProgress(prev => ({
      ...prev,
      ...data
    }));
  };
  const updateMentalState = (data: Partial<MentalState>) => {
    setMentalState(prev => ({
      ...prev,
      ...data
    }));
  };
  const addActivity = (activity: UserActivity) => {
    setRecentActivities(prev => [activity, ...prev]);
  };
  return <UserDataContext.Provider value={{
    userProgress,
    mentalState,
    recentActivities,
    updateUserProgress,
    updateMentalState,
    addActivity
  }} data-id="element-1488">
      {children}
    </UserDataContext.Provider>;
};
// Custom hook for using the context
export const useUserData = () => useContext(UserDataContext);